import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';

/**
 * Linear's list for records that stay put — associations, vendors — rather
 * than work that moves through states. `GroupedList` groups by status and
 * reads as a queue; this is one flat, sorted directory: quiet column
 * headers, a monogram and the name leading each row, the record's
 * properties in aligned columns, and an inactive record dimmed rather than
 * flagged in red.
 *
 * The header stays put and the rows scroll beneath it when a parent gives
 * the list a height (`min-h-0 flex-1` in a flex column); otherwise the list
 * simply grows. Columns carry their own width and breakpoint, applied to
 * header and cells alike, so a narrow panel drops columns, never wraps them.
 */
export interface DirectoryColumn<T> {
  key: string;
  label: string;
  /** Width and visibility, e.g. `'w-40 hidden lg:block'`. */
  className: string;
  render: (item: T) => React.ReactNode;
  /** Makes the column sortable by its header. */
  sortValue?: (item: T) => string;
}

interface Props<T> {
  items: T[];
  getKey: (item: T) => string;
  getName: (item: T) => string;
  /** Dims the row — an inactive association, a vendor no longer used. */
  isMuted?: (item: T) => boolean;
  columns: DirectoryColumn<T>[];
  /** Opens the record. Without it — a record with no page — rows are not buttons. */
  onOpen?: (item: T) => void;
  /** A row's own controls, at its far end — a "…" menu. Kept outside the row's button. */
  actions?: (item: T) => React.ReactNode;
  /** Round monograms, for people; square ones are for places and businesses. */
  people?: boolean;
  emptyMessage: string;
}

interface Sort {
  key: string;
  desc: boolean;
}

const NAME_KEY = 'name';

const compare = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/** "Oak Hollow Estates" → "OH"; "Acme" → "AC". */
export const monogram = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letters =
    words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return letters.toUpperCase();
};

/** A record's standing as a dot and a word — Active, Inactive. */
export const StatusCell: React.FC<{ active: boolean; label: string }> = ({ active, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      className={clsx(
        'h-2 w-2 shrink-0 rounded-full',
        active ? 'bg-success' : 'border border-fg-muted'
      )}
      aria-hidden
    />
    <span className={active ? 'text-fg-secondary' : 'text-fg-muted'}>{label}</span>
  </span>
);

/** An empty property: a quiet dash, not a blank that looks unloaded. */
export const EMPTY_CELL = <span className="text-fg-faint">—</span>;

export function DirectoryList<T>({
  items,
  getKey,
  getName,
  isMuted,
  columns,
  onOpen,
  actions,
  people = false,
  emptyMessage,
}: Props<T>) {
  const [sort, setSort] = useState<Sort>({ key: NAME_KEY, desc: false });

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort.key);
    const value = column?.sortValue ?? getName;
    const out = [...items].sort(
      (a, b) => compare(value(a), value(b)) || compare(getName(a), getName(b))
    );
    return sort.desc ? out.reverse() : out;
  }, [items, columns, sort, getName]);

  const sortBy = (key: string) =>
    setSort((s) => ({ key, desc: s.key === key ? !s.desc : false }));

  const header = (key: string, label: string, sortable: boolean) => {
    if (!sortable) return label;
    const active = sort.key === key;
    const Arrow = active && sort.desc ? ChevronDownIcon : ChevronUpIcon;
    return (
      <button
        type="button"
        onClick={() => sortBy(key)}
        aria-label={`Sort by ${label.toLowerCase()}`}
        aria-pressed={active}
        className={clsx(
          'inline-flex items-center gap-1 rounded hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          active && 'text-fg-secondary'
        )}
      >
        {label}
        <Arrow className={clsx('h-3 w-3', !active && 'invisible')} aria-hidden />
      </button>
    );
  };

  if (items.length === 0) {
    return <p className="px-4 py-10 text-center text-[13px] text-fg-muted">{emptyMessage}</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-4 border-b border-line/60 px-4 text-[12px] text-fg-muted">
        <span className="min-w-0 flex-1 pl-9">{header(NAME_KEY, 'Name', true)}</span>
        {columns.map((c) => (
          <span key={c.key} className={clsx('shrink-0', c.className)}>
            {header(c.key, c.label, !!c.sortValue)}
          </span>
        ))}
        {actions && <span className="w-7 shrink-0" aria-hidden />}
      </div>
      <ul className="min-h-0 flex-1 divide-y divide-line/50 overflow-y-auto">
        {sorted.map((item) => {
          const muted = isMuted?.(item) ?? false;
          const name = getName(item);
          const cells = (
            <>
              <span className="flex min-w-0 flex-1 items-center gap-3">
                <span
                  className={clsx(
                    'flex h-6 w-6 shrink-0 items-center justify-center bg-fg/[0.07] text-[10px] font-semibold tracking-wide text-fg-secondary',
                    people ? 'rounded-full' : 'rounded-md',
                    muted && 'opacity-60'
                  )}
                  aria-hidden
                >
                  {monogram(name)}
                </span>
                <span className={clsx('truncate font-medium', muted ? 'text-fg-muted' : 'text-fg')}>
                  {name}
                </span>
              </span>
              {columns.map((c) => (
                <span
                  key={c.key}
                  className={clsx('shrink-0 truncate', muted ? 'text-fg-muted' : 'text-fg-secondary', c.className)}
                >
                  {c.render(item)}
                </span>
              ))}
            </>
          );
          const rowClass = 'flex h-11 min-w-0 flex-1 items-center gap-4 text-left text-[13px]';
          return (
            <li key={getKey(item)} className="flex items-center gap-4 px-4 hover:bg-fg/[0.03]">
              {onOpen ? (
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  aria-label={name}
                  className={clsx(rowClass, 'focus:outline-none focus-visible:bg-fg/5')}
                >
                  {cells}
                </button>
              ) : (
                <div className={rowClass}>{cells}</div>
              )}
              {actions && (
                <span className="flex w-7 shrink-0 justify-end">{actions(item)}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
